import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';

const helpers = createMultiStyleConfigHelpers([
  'root',
  'header',
  'title',
  'selectedLabel',
  'selectedValue',
  'grid',
  'description',
  'tile',
  'tileImageArea',
  'tileImage',
  'tileImagePlaceholder',
  'tileLabelArea',
  'tileLabel',
  'tileNameLine1',
  'tileNameLine2',
  'tileDescription',
  'tileViewDetails',
  'tileStockMessage',
  'tilePriceMessage',
  'tileStockMessageOOS',
  'tileStockMessageLow',
  'tileStockBadge',
  'tileStockBadgeLow',
  'tileStockBadgeOOS',
  'tileConfigurableBadge',
  'tileOOSOverlayLine',
  'moreCta',
  'moreCtaImageArea',
  'moreCtaIcon',
  'moreCtaLabel',
  'moreCtaCtaRow',
  'moreCtaCaret',
  'moreCtaCount',
  'moreCtaMore',
]);

/** Shared product-tag badge positioning (Figma product tags). */
const tileStockBadgeBase = {
  border: 'none',
  borderRadius: '4px',
  fontSize: '12px',
  fontWeight: 600,
  left: {base: '5.5px', lg: '7px'},
  top: {base: '5.5px', lg: '7px'},
  lineHeight: 1.2,
  position: 'absolute',
  px: {base: '4px', lg: '4px'},
  py: {base: '2px', lg: '4px'},
  textAlign: 'center',
  textTransform: 'none',
};

/**
 * @typedef {import('@chakra-ui/styled-system').PartsStyleInterpolation<{
 *   keys: (
 *     "root" | "header" | "title" | "selectedLabel" | "selectedValue" |
 *     "grid" | "description" | "tile" | "tileImageArea" | "tileImage" | "tileImagePlaceholder" |
 *     "tileLabelArea" | "tileLabel" | "tileNameLine1" | "tileNameLine2" | "tileDescription" |
 *     "tileViewDetails" |
 *     "tileStockMessage" | "tilePriceMessage" | "tileStockMessageOOS" | "tileStockMessageLow" |
 *     "tileStockBadge" | "tileStockBadgeLow" | "tileStockBadgeOOS" |
 *     "tileConfigurableBadge" | "tileOOSOverlayLine" |
 *     "moreCta" | "moreCtaImageArea" | "moreCtaIcon" | "moreCtaLabel" | "moreCtaCtaRow" |
 *     "moreCtaCaret" | "moreCtaCount" | "moreCtaMore"
 *   )[]
 * }>} CoverSwatchGridStyles
 */
const CoverSwatchGrid = helpers.defineMultiStyleConfig({
  baseStyle: ({isSelected, isOutOfStock}) => ({
    root: {
      mb: 6,
      mt: 4,
      w: 'full',
    },
    header: {
      alignItems: 'baseline',
      display: 'flex',
      gap: 2,
      mb: {base: '16px', lg: '16px'},
    },
    title: {
      color: 'brandColors.greyscale',
      fontSize: {base: '16px', lg: '18px'},
      fontWeight: 500,
      letterSpacing: 'normal',
      lineHeight: {base: 1.4, lg: 1.2},
      textTransform: 'none',
    },
    selectedLabel: {
      color: 'gray.500',
      fontSize: 'sm',
      ml: 1,
    },
    selectedValue: {
      color: 'gray.900',
      fontSize: 'sm',
      fontWeight: 'semibold',
    },
    grid: {
      columns: {base: 3, xsm: 4, md: 8, lg: 4},
      spacing: '4px',
    },
    // Disclaimer note below the cover grid (FSD: "Choose your cover").
    description: {
      color: 'brandColors.greyscale',
      fontSize: {base: '14px', lg: '14px'},
      fontWeight: 400,
      lineHeight: 1.5,
      mt: {base: '16px', lg: '16px'},
      textAlign: 'left',
      w: 'full',
    },
    tile: {
      bg: 'white',
      borderWidth: isSelected ? '2px' : '1px',
      borderStyle: 'solid',
      borderColor: isSelected ? 'gray.800' : 'grayAlpha.10',
      borderRadius: '6px',
      cursor: isOutOfStock ? 'not-allowed' : 'pointer',
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
      overflow: 'hidden',
      pb: 0,
      position: 'relative',
      w: 'full',
      ...(!isSelected && !isOutOfStock && {
        _hover: {
          borderColor: 'grayAlpha.90',
        },
      }),
      _focusVisible: {
        outline: '2px solid',
        outlineColor: 'gray.900',
        outlineOffset: '2px',
      },
    },
    tileImageArea: {
      aspectRatio: '1 / 1',
      borderBottomRadius: 0,
      borderColor: isSelected ? 'white' : 'transparent',
      borderStyle: 'solid',
      borderTopRadius: '3px',
      borderWidth: isSelected ? '2px' : '0',
      borderBottomWidth: 0,
      overflow: 'hidden',
      p: 0,
      position: 'relative',
      w: 'full',
    },
    tileImage: {
      borderBottomRadius: 0,
      borderTopRadius: '3px',
      h: '100%',
      objectFit: 'cover',
      w: 'full',
    },
    tileImagePlaceholder: {
      alignItems: 'center',
      bg: 'gray.100',
      borderBottomRadius: 0,
      borderTopRadius: '3px',
      display: 'flex',
      h: 'full',
      justifyContent: 'center',
      w: 'full',
    },
    tileLabelArea: {
      alignItems: 'stretch',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      px: '4px',
      py: '6px',
      textAlign: 'center',
      w: 'full',
    },
    tileLabel: {
      color: 'black',
      fontSize: {base: '13px', lg: '12px'},
      fontWeight: 600,
      lineHeight: 1.2,
      noOfLines: 2,
    },
    tileNameLine1: {
      color: 'brandColors.greyscale',
      fontFamily: 'body',
      fontSize: {base: '11px', lg: '12px'},
      fontVariantNumeric: 'lining-nums proportional-nums',
      fontWeight: 600,
      lineHeight: {base: 1.2, lg: 1.4},
      textAlign: 'center',
      w: 'full',
    },
    tileNameLine2: {
      color: 'greyscale.70',
      fontFamily: 'body',
      fontSize: {base: '11px', lg: '12px'},
      fontWeight: 400,
      lineHeight: 1.2,
      textAlign: 'center',
      w: 'full',
    },
    tileDescription: {
      color: 'black',
      fontSize: {base: '11px', lg: '12px'},
      fontWeight: 400,
      lineHeight: 1.2,
    },
    tileViewDetails: {
      color: 'brandColors.viewDetailsLink',
      cursor: 'pointer',
      display: {base: 'block', lg: 'none'},
      fontSize: {base: '11px', lg: '12px'},
      fontWeight: 600,
      lineHeight: 1.2,
      textAlign: 'center',
      whiteSpace: 'nowrap',
      marginLeft: {base: '-3px', lg: '0'},
      _hover: {
        color: 'brandColors.viewDetailsLink',
      },
      _focusVisible: {
        outline: '2px solid',
        outlineColor: 'brandColors.viewDetailsLink',
        outlineOffset: '2px',
      },
    },
    tileStockMessage: {
      color: 'success.dark',
      fontSize: {base: '11px', lg: '12px'},
      fontWeight: 400,
      lineHeight: 1.2,
      textAlign: 'center',
      w: 'full',
    },
    tilePriceMessage: {
      color: 'greyscale.70',
      fontSize: {base: '11px', lg: '12px'},
      fontWeight: 400,
      lineHeight: 1.2,
      textAlign: 'center',
      w: 'full',
    },
    tileStockMessageLow: {
      color: 'warning.dark',
      fontSize: {base: '11px', lg: '12px'},
      fontWeight: 400,
      lineHeight: 1.2,
      textAlign: 'center',
      w: 'full',
    },
    tileStockMessageOOS: {
      color: 'error.standard',
      fontSize: {base: '11px', lg: '12px'},
      fontWeight: 400,
      lineHeight: 1.2,
      textAlign: 'center',
      w: 'full',
    },
    tileStockBadge: {
      ...tileStockBadgeBase,
      bg: 'success.background',
      color: 'success.dark',
    },
    tileStockBadgeLow: {
      ...tileStockBadgeBase,
      bg: 'warning.background',
      color: 'warning.dark',
    },
    tileStockBadgeOOS: {
      ...tileStockBadgeBase,
      bg: 'error.background',
      color: 'error.dark',
    },
    tileConfigurableBadge: {
      ...tileStockBadgeBase,
      bg: 'gray.900',
      color: 'white',
    },
    // Sold-out diagonal (Figma 7730:61429) — ~135deg, 6px band (2px white / 2px black / 2px white).
    tileOOSOverlayLine: {
      background: 'linear-gradient(to bottom, #FFFFFF 0, #FFFFFF 2px, #000000 2px, #000000 4px, #FFFFFF 4px, #FFFFFF 6px)',
      height: '6px',
      left: '50%',
      pointerEvents: 'none',
      position: 'absolute',
      top: '50%',
      transform: 'translate(-50%, -50%) rotate(135deg)',
      transformOrigin: 'center',
      width: {base: '58px', lg: '72px'},
    },
    moreCta: {
      alignItems: 'stretch',
      bg: 'white',
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: 'grayAlpha.10',
      borderRadius: '6px',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      gap: 0,
      overflow: 'hidden',
      w: 'full',
      _hover: {
        borderColor: 'grayAlpha.90',
      },
      _focusVisible: {
        outline: '2px solid',
        outlineColor: 'gray.900',
        outlineOffset: '2px',
      },
    },
    moreCtaImageArea: {
      alignItems: 'flex-end',
      aspectRatio: '1 / 1',
      borderBottomRadius: 0,
      borderTopRadius: '3px',
      display: 'flex',
      justifyContent: 'center',
      overflow: 'hidden',
      pb: {base: '12px', lg: '14px'},
      w: 'full',
    },
    moreCtaIcon: {
      color: 'gray.800',
      flexShrink: 0,
      h: '40px',
      w: '41px',
    },
    moreCtaLabel: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      pb: '6px',
      pt: 0,
      px: '4px',
      textAlign: 'center',
      w: 'full',
    },
    moreCtaCtaRow: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'row',
      gap: '3px',
      justifyContent: 'center',
      w: 'full',
    },
    moreCtaCaret: {
      boxSize: '10px',
      color: 'brandColors.greyscale',
      flexShrink: 0,
    },
    moreCtaCount: {
      color: 'brandColors.greyscale',
      fontSize: {base: '11px', lg: '12px'},
      fontWeight: 500,
      lineHeight: 1.2,
      textAlign: 'center',
    },
    moreCtaMore: {
      color: 'greyscale.70',
      fontSize: {base: '11px', lg: '12px'},
      fontWeight: 400,
      lineHeight: 1.2,
      textAlign: 'center',
      w: 'full',
    },
  }),
});

export default CoverSwatchGrid;
